/**
 * OrderTable — desktop table view for orders list.
 */

import type { OrderSummary, OrderAttentionFlags } from "../api";
import type { OrderScore } from "@/features/performance/api";
import { useDataVisibility } from "@/lib/authv2/permissions";
import { OrderRow } from "./OrderRow";

interface Props {
  orders: OrderSummary[];
  /** Cờ nhắc theo mã đơn. Vắng mặt (chưa hỏi xong / hỏi hỏng) là bình thường. */
  attentionFlags?: OrderAttentionFlags | undefined;
  /** Nhãn điểm theo mã đơn. Map rỗng khi chưa chấm hoặc không có quyền xem. */
  scores?: Map<string, OrderScore> | undefined;
}

export function OrderTable({ orders, attentionFlags, scores }: Props) {
  // QĐ 0132 câu 1a: người không được xem tiền (thợ chụp / trang điểm / sửa
  // ảnh) không có ba cột tiền — máy chủ đã đưa số về 0, vẽ ra là cột "0 đ".
  // Hàng (OrderRow) bỏ đúng ba ô ấy theo cùng cờ.
  const { orderMoney } = useDataVisibility();
  if (orders.length === 0) {
    return (
      <div className="rounded-md border bg-surface p-8 text-center text-sm text-muted-foreground">
        Không có đơn hàng nào phù hợp.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border bg-surface">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
            {/* Cột "Mã đơn" đã bỏ (CEO chốt 01/09/2026): mã của đơn di trú là
                chuỗi máy 20 ký tự, đọc không ra nghĩa mà ăn hết bề ngang.
                Mã vẫn nằm ở đầu trang chi tiết đơn và vẫn gõ tìm được ở ô
                Tìm kiếm (features/orders/filters.ts). */}
            <th className="whitespace-nowrap px-4 py-2 font-medium">Khách hàng</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">Sản phẩm</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">Chi nhánh</th>
            <th className="whitespace-nowrap px-4 py-2 font-medium">Ngày chụp</th>
            {orderMoney && (
              <>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-right">Tổng tiền</th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-right">Cọc</th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-right">
                  PS Trang phục
                </th>
              </>
            )}
            <th className="whitespace-nowrap px-4 py-2 font-medium">Trạng thái</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <OrderRow
              key={o.id}
              order={o}
              flags={attentionFlags?.[o.id]}
              score={scores?.get(o.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
